/** Shape of a price object from api.subscription.getConfig (Stripe price + product) */
export interface ConfigPrice {
  id: string;
  unit_amount: number;
  metadata?: Record<string, string>;
  recurring?: { interval: string; interval_count: number };
  product?: {
    id?: string;
    metadata?: Record<string, string>;
    name?: string;
    description?: string;
    features?: unknown;
    marketing_features?: unknown;
  };
}

export type RenewalOption = "prepay" | "renewLater";
export type ServiceOption = "privacyCore" | "privacyPremium";
export type BundleOption =
  | "heroOnly"
  | "heroNordVPNStandard"
  | "heroNordVPNPlus";
export type TermOption = "oneYear" | "twoYear" | "monthly";

const PREPAID_COUPON_RE =
  /^PREPAID_(1YEAR|2YEAR)_(CORE|PREMIUM)(NORDPLUS|NORD)?$/;

const VALID_ACCESS_TIERS = new Set(["core", "premium"]);
const VALID_NORD_TIERS = new Set(["standard", "plus"]);

/** When `reason` is set, only prices whose product.metadata[reason] is "true" are used. */
export function filterPricesByReason(
  prices: ConfigPrice[],
  reason?: string
): ConfigPrice[] {
  if (!reason) return prices;
  return prices.filter(
    (p) => String(p.product?.metadata?.[reason] ?? "").toLowerCase() === "true"
  );
}

/**
 * Maps form selections to the expected APPLIED_COUPON value for prepaid prices.
 * Format: PREPAID_<TERM>_<SERVICE>[NORD|NORDPLUS]
 */
export function getExpectedPrepaidCoupon(
  termSel: TermOption,
  serviceSel: ServiceOption,
  bundleSel: BundleOption
): string {
  const termPart = termSel === "oneYear" ? "1YEAR" : "2YEAR";
  const servicePart = serviceSel === "privacyCore" ? "CORE" : "PREMIUM";
  const nordPart =
    bundleSel === "heroOnly"
      ? ""
      : bundleSel === "heroNordVPNStandard"
      ? "NORD"
      : "NORDPLUS";
  return `PREPAID_${termPart}_${servicePart}${nordPart}`;
}

/** Prepaid one-time prices whose APPLIED_COUPON matches the known plan matrix. */
export function isPertinentPrepaidPrice(price: ConfigPrice): boolean {
  if (price.recurring) return false;
  const coupon = (price.metadata?.APPLIED_COUPON ?? "").toUpperCase();
  return PREPAID_COUPON_RE.test(coupon);
}

/** Recurring prices that match the known ACCESS_TIER / NORD_TIER / term matrix. */
export function isPertinentRecurringPrice(price: ConfigPrice): boolean {
  const rec = price.recurring;
  if (!rec) return false;

  const accessTier = (price.product?.metadata?.ACCESS_TIER ?? "").toLowerCase();
  if (!VALID_ACCESS_TIERS.has(accessTier)) return false;

  const nordRaw = price.product?.metadata?.NORD_TIER;
  const nordTier = nordRaw && String(nordRaw).trim() ? String(nordRaw) : null;
  if (nordTier != null && !VALID_NORD_TIERS.has(nordTier.toLowerCase())) {
    return false;
  }

  const interval = rec.interval;
  const count = Number(rec.interval_count);
  if (interval === "month" && count === 1) return true;
  if (interval === "year" && (count === 1 || count === 2)) return true;
  return false;
}

export interface DisplayablePlanPricesOptions {
  recurringOnly?: boolean;
  reason?: string;
}

/** Prices that would have been selectable via the old Q&A matcher. */
export function getDisplayablePlanPrices(
  prices: ConfigPrice[],
  options: DisplayablePlanPricesOptions = {}
): ConfigPrice[] {
  const { recurringOnly = false, reason } = options;
  const filtered = filterPricesByReason(prices, reason);
  return filtered.filter((p) => {
    if (recurringOnly) return isPertinentRecurringPrice(p);
    return isPertinentPrepaidPrice(p) || isPertinentRecurringPrice(p);
  });
}

/**
 * Parse a prepaid APPLIED_COUPON into service / bundle / term selections.
 * Returns null when the coupon is not in the known matrix.
 */
export function parsePrepaidCoupon(coupon: string): {
  service: ServiceOption;
  bundle: BundleOption;
  term: TermOption;
} | null {
  const match = coupon.toUpperCase().match(PREPAID_COUPON_RE);
  if (!match) return null;
  const [, termPart, servicePart, nordPart] = match;
  return {
    term: termPart === "1YEAR" ? "oneYear" : "twoYear",
    service: servicePart === "CORE" ? "privacyCore" : "privacyPremium",
    bundle:
      nordPart === "NORDPLUS"
        ? "heroNordVPNPlus"
        : nordPart === "NORD"
        ? "heroNordVPNStandard"
        : "heroOnly",
  };
}

/** Recurring twin for a prepaid price (used by changePlan before one-time checkout). */
export function getRecurringTwinForPrepaid(
  prepaid: ConfigPrice,
  priceList: ConfigPrice[]
): ConfigPrice | null {
  const coupon = prepaid.metadata?.APPLIED_COUPON;
  if (!coupon) return null;
  const parsed = parsePrepaidCoupon(coupon);
  if (!parsed) return null;
  return getMatchingRecurringPrice(
    parsed.service,
    parsed.bundle,
    parsed.term,
    priceList
  );
}

export function isPrepaidPrice(price: ConfigPrice): boolean {
  return isPertinentPrepaidPrice(price);
}

export interface ClassifiedPlanPrice {
  accessTier: ServiceOption;
  bundle: BundleOption;
  term: TermOption;
  renewal: RenewalOption;
}

const BUNDLE_RANK: Record<BundleOption, number> = {
  heroOnly: 1,
  heroNordVPNStandard: 2,
  heroNordVPNPlus: 3,
};

const TERM_RANK: Record<TermOption, number> = {
  monthly: 1,
  oneYear: 2,
  twoYear: 3,
};

const BUNDLE_SORT_ORDER: Record<BundleOption, number> = {
  heroOnly: 0,
  heroNordVPNStandard: 1,
  heroNordVPNPlus: 2,
};

function nordTierToBundle(nordRaw: string | null | undefined): BundleOption {
  if (!nordRaw || !String(nordRaw).trim()) return "heroOnly";
  const lower = String(nordRaw).toLowerCase();
  if (lower === "plus") return "heroNordVPNPlus";
  if (lower === "standard") return "heroNordVPNStandard";
  return "heroOnly";
}

function recurringTerm(price: ConfigPrice): TermOption | null {
  const rec = price.recurring;
  if (!rec) return null;
  const count = Number(rec.interval_count);
  if (rec.interval === "month" && count === 1) return "monthly";
  if (rec.interval === "year" && count === 1) return "oneYear";
  if (rec.interval === "year" && count === 2) return "twoYear";
  return null;
}

/** Classify a pertinent price into access tier / bundle / term / renewal. */
export function classifyPlanPrice(
  price: ConfigPrice
): ClassifiedPlanPrice | null {
  if (isPertinentPrepaidPrice(price)) {
    const parsed = parsePrepaidCoupon(price.metadata?.APPLIED_COUPON ?? "");
    if (!parsed) return null;
    return {
      accessTier: parsed.service,
      bundle: parsed.bundle,
      term: parsed.term,
      renewal: "prepay",
    };
  }
  if (isPertinentRecurringPrice(price)) {
    const accessRaw = (
      price.product?.metadata?.ACCESS_TIER ?? ""
    ).toLowerCase();
    if (accessRaw !== "core" && accessRaw !== "premium") return null;
    const term = recurringTerm(price);
    if (!term) return null;
    return {
      accessTier: accessRaw === "core" ? "privacyCore" : "privacyPremium",
      bundle: nordTierToBundle(price.product?.metadata?.NORD_TIER),
      term,
      renewal: "renewLater",
    };
  }
  return null;
}

export function filterPricesForRenewal(
  prices: ConfigPrice[],
  renewal: RenewalOption
): ConfigPrice[] {
  return prices.filter((p) => {
    const classified = classifyPlanPrice(p);
    return classified?.renewal === renewal;
  });
}

export type PlanDisplaySection = {
  accessTier: ServiceOption;
  terms: Partial<Record<TermOption, ConfigPrice[]>>;
};

/** Group prices into Core/Premium sections with term columns, sorted by bundle. */
export function groupPlanPricesForDisplay(
  prices: ConfigPrice[]
): PlanDisplaySection[] {
  const buckets: Record<
    ServiceOption,
    Partial<Record<TermOption, ConfigPrice[]>>
  > = {
    privacyCore: {},
    privacyPremium: {},
  };

  for (const price of prices) {
    const classified = classifyPlanPrice(price);
    if (!classified) continue;
    const termBucket = buckets[classified.accessTier][classified.term] ?? [];
    termBucket.push(price);
    buckets[classified.accessTier][classified.term] = termBucket;
  }

  const sortTerm = (list: ConfigPrice[]) =>
    list.sort((a, b) => {
      const ca = classifyPlanPrice(a);
      const cb = classifyPlanPrice(b);
      return (
        (BUNDLE_SORT_ORDER[ca?.bundle ?? "heroOnly"] ?? 0) -
        (BUNDLE_SORT_ORDER[cb?.bundle ?? "heroOnly"] ?? 0)
      );
    });

  const sections: PlanDisplaySection[] = [];
  for (const accessTier of [
    "privacyCore",
    "privacyPremium",
  ] as ServiceOption[]) {
    const terms = buckets[accessTier];
    const hasAny = Object.values(terms).some((list) => list && list.length > 0);
    if (!hasAny) continue;
    for (const term of Object.keys(terms) as TermOption[]) {
      if (terms[term]) sortTerm(terms[term]!);
    }
    sections.push({ accessTier, terms });
  }
  return sections;
}

export interface PlanSavings {
  regularAmount: number;
  saveAmount: number;
  savePercent: number;
  regularPrice: ConfigPrice;
}

/** Compare prepaid amount to its recurring twin; null when no savings. */
export function getSavingsVsRegular(
  prepaid: ConfigPrice,
  allPrices: ConfigPrice[]
): PlanSavings | null {
  if (!isPertinentPrepaidPrice(prepaid)) return null;
  const twin = getRecurringTwinForPrepaid(prepaid, allPrices);
  if (!twin || twin.unit_amount <= prepaid.unit_amount) return null;
  const saveAmount = twin.unit_amount - prepaid.unit_amount;
  const savePercent = Math.round((saveAmount / twin.unit_amount) * 100);
  return {
    regularAmount: twin.unit_amount,
    saveAmount,
    savePercent,
    regularPrice: twin,
  };
}

export type PlanBadgeKind = "mostPopular" | "bestValue";

/**
 * Hardcoded: badge the highest-tier plan in each access-tier section.
 * Core → mostPopular; Premium → bestValue.
 * Rank: Nord Plus > Nord Standard > Hero Only; term tie-break 2yr > 1yr > monthly.
 */
export function getHighestTierBadgePriceIds(
  sections: PlanDisplaySection[]
): Record<string, PlanBadgeKind> {
  const result: Record<string, PlanBadgeKind> = {};

  for (const section of sections) {
    const candidates: ConfigPrice[] = [];
    for (const list of Object.values(section.terms)) {
      if (list) candidates.push(...list);
    }
    if (candidates.length === 0) continue;

    let best: ConfigPrice | null = null;
    let bestScore = -1;
    for (const price of candidates) {
      const c = classifyPlanPrice(price);
      if (!c) continue;
      const score = BUNDLE_RANK[c.bundle] * 10 + TERM_RANK[c.term];
      if (score > bestScore) {
        bestScore = score;
        best = price;
      }
    }
    if (best) {
      result[best.id] =
        section.accessTier === "privacyCore" ? "mostPopular" : "bestValue";
    }
  }

  return result;
}

export function formatMoneyFromCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function featureEntryToName(entry: unknown): string | null {
  if (typeof entry === "string") {
    const trimmed = entry.trim();
    return trimmed || null;
  }
  if (entry && typeof entry === "object" && "name" in entry) {
    const name = String((entry as { name?: unknown }).name ?? "").trim();
    return name || null;
  }
  return null;
}

function asFeatureArray(raw: unknown): unknown[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  // Stripe list-shaped payloads: { data: [...] }
  if (typeof raw === "object" && raw !== null && "data" in raw) {
    const data = (raw as { data?: unknown }).data;
    if (Array.isArray(data)) return data;
  }
  return [];
}

function namesFromFeatureRaw(raw: unknown): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const entry of asFeatureArray(raw)) {
    const name = featureEntryToName(entry);
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}

function resolveExpandedProduct(
  price: ConfigPrice,
  catalog: ConfigPrice[]
): ConfigPrice["product"] | null {
  const product = price.product as ConfigPrice["product"] | string | undefined;
  if (!product) return null;
  if (typeof product === "string") {
    for (const other of catalog) {
      if (other.product?.id === product) return other.product;
    }
    return null;
  }
  return product;
}

const METADATA_FEATURE_LABELS: Record<string, string> = {
  VPN: "NordLynx VPN",
  PHERO_VPN: "Privacy Hero VPN",
  STREAMING_RELOCATION: "Streaming Relocation",
  DEVICES: "Device Management",
  PROFILES: "User Device Profiles",
  DATA: "Data Visualization",
  AD_BLOCKING: "Ad Blocking",
  PARENTAL_CONTROLS: "Parental Controls",
  MALWARE: "Malware & Phishing Defense",
  WIREGUARD: "3rd-Party WireGuard VPN",
};

const METADATA_FEATURE_SKIP = new Set([
  "owner",
  "access_tier",
  "nord_tier",
  "included_code",
]);

function namesFromProductMetadata(metadata?: Record<string, string>): string[] {
  if (!metadata) return [];
  const names: string[] = [];
  for (const [key, value] of Object.entries(metadata)) {
    if (String(value).toLowerCase() !== "true") continue;
    if (METADATA_FEATURE_SKIP.has(key.toLowerCase())) continue;
    names.push(
      METADATA_FEATURE_LABELS[key] ??
        key
          .split("_")
          .map(
            (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          )
          .join(" ")
    );
  }
  return names;
}

/** Normalize product feature bullets from Stripe-style price payloads. */
export function extractFeatureNamesFromPrice(
  price: ConfigPrice,
  catalog: ConfigPrice[] = []
): string[] {
  const product = resolveExpandedProduct(price, catalog);
  if (!product) return [];

  const fromFeatures = namesFromFeatureRaw(product.features);
  if (fromFeatures.length > 0) return fromFeatures;

  const fromMarketing = namesFromFeatureRaw(product.marketing_features);
  if (fromMarketing.length > 0) return fromMarketing;

  // Same product id elsewhere in the catalog may carry the marketing feature list.
  if (product.id && catalog.length > 0) {
    for (const other of catalog) {
      if (!other.product || other.product.id !== product.id) continue;
      const names = [
        ...namesFromFeatureRaw(other.product.features),
        ...namesFromFeatureRaw(other.product.marketing_features),
      ];
      if (names.length > 0) {
        return Array.from(new Set(names));
      }
    }
  }

  // Backend list-features treats product.metadata[KEY]==="true" as included features.
  const fromMetadata = namesFromProductMetadata(product.metadata);
  if (fromMetadata.length > 0) return fromMetadata;

  return [];
}

/**
 * Feature bullets for display. Prefers the price's own product features;
 * for prepaid prices with an empty list, falls back to the recurring twin
 * (and then any same-tier catalog price that has features).
 */
export function getPriceFeatureNames(
  price: ConfigPrice,
  catalog: ConfigPrice[] = []
): string[] {
  const own = extractFeatureNamesFromPrice(price, catalog);
  if (own.length > 0) return own;

  if (isPertinentPrepaidPrice(price)) {
    const twin = getRecurringTwinForPrepaid(price, catalog);
    if (twin) {
      const twinFeatures = extractFeatureNamesFromPrice(twin, catalog);
      if (twinFeatures.length > 0) return twinFeatures;
    }
  }

  const classified = classifyPlanPrice(price);
  if (!classified || catalog.length === 0) return [];

  for (const candidate of catalog) {
    const other = classifyPlanPrice(candidate);
    if (
      !other ||
      other.accessTier !== classified.accessTier ||
      other.bundle !== classified.bundle
    ) {
      continue;
    }
    const names = extractFeatureNamesFromPrice(candidate, catalog);
    if (names.length > 0) return names;
  }

  return [];
}

/**
 * Finds the single price that matches the user's selections (renewal type, service, bundle, term).
 */
export function getMatchingPrice(
  renewalSel: RenewalOption,
  serviceSel: ServiceOption | null,
  bundleSel: BundleOption | null,
  termSel: TermOption | null,
  priceList: ConfigPrice[]
): ConfigPrice | null {
  if (!serviceSel || !bundleSel || !termSel || priceList.length === 0)
    return null;

  if (renewalSel === "prepay") {
    const expectedCoupon = getExpectedPrepaidCoupon(
      termSel,
      serviceSel,
      bundleSel
    );
    const match = priceList.find(
      (p) => (p.metadata?.APPLIED_COUPON ?? "").toUpperCase() === expectedCoupon
    );
    return match ?? null;
  }

  const accessTier = serviceSel === "privacyCore" ? "Core" : "Premium";
  const wantNordTier: string | null =
    bundleSel === "heroOnly"
      ? null
      : bundleSel === "heroNordVPNStandard"
      ? "Standard"
      : "Plus";
  const wantInterval = termSel === "monthly" ? "month" : "year";
  const wantIntervalCount =
    termSel === "monthly" ? 1 : termSel === "oneYear" ? 1 : 2;

  const match = priceList.find((p) => {
    const productTier = (p.product?.metadata?.ACCESS_TIER ?? "").toLowerCase();
    if (productTier !== accessTier.toLowerCase()) return false;
    const rec = p.recurring;
    if (
      !rec ||
      rec.interval !== wantInterval ||
      Number(rec.interval_count) !== wantIntervalCount
    )
      return false;
    const nordRaw = p.product?.metadata?.NORD_TIER;
    const productNordTier =
      nordRaw && String(nordRaw).trim() ? String(nordRaw) : null;
    if (wantNordTier === null) return !productNordTier;
    return (
      !!productNordTier &&
      productNordTier.toLowerCase() === wantNordTier.toLowerCase()
    );
  });
  return match ?? null;
}

/** Recurring price for (service, bundle, term) — used for changePlan when prepay flow switches subscription to recurring first. */
export function getMatchingRecurringPrice(
  serviceSel: ServiceOption | null,
  bundleSel: BundleOption | null,
  termSel: TermOption | null,
  priceList: ConfigPrice[]
): ConfigPrice | null {
  if (!serviceSel || !bundleSel || !termSel || priceList.length === 0)
    return null;
  const accessTier = serviceSel === "privacyCore" ? "Core" : "Premium";
  const wantNordTier: string | null =
    bundleSel === "heroOnly"
      ? null
      : bundleSel === "heroNordVPNStandard"
      ? "Standard"
      : "Plus";
  const wantInterval = termSel === "monthly" ? "month" : "year";
  const wantIntervalCount =
    termSel === "monthly" ? 1 : termSel === "oneYear" ? 1 : 2;
  const match = priceList.find((p) => {
    if (!p.recurring) return false;
    const productTier = (p.product?.metadata?.ACCESS_TIER ?? "").toLowerCase();
    if (productTier !== accessTier.toLowerCase()) return false;
    const rec = p.recurring;
    if (
      !rec ||
      rec.interval !== wantInterval ||
      Number(rec.interval_count) !== wantIntervalCount
    )
      return false;
    const nordRaw = p.product?.metadata?.NORD_TIER;
    const productNordTier =
      nordRaw && String(nordRaw).trim() ? String(nordRaw) : null;
    if (wantNordTier === null) return !productNordTier;
    return (
      !!productNordTier &&
      productNordTier.toLowerCase() === wantNordTier.toLowerCase()
    );
  });
  return match ?? null;
}
